import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  FireIcon,
  BoltIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ArrowPathIcon,
  LockClosedIcon,
  ChartBarIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/solid';
import PlayerDetailModal from '../components/streaks/PlayerDetailModal';
import PremiumGateModal from '../components/common/PremiumGateModal';
import TeamStreaksTable from '../components/dashboard/TeamStreaksTable';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import playerDetailsService from '../services/playerDetailsService';

// Sport configuration - fallback if API fails
const DEFAULT_SPORTS = [
  { id: 'nhl', name: 'NHL' },
  { id: 'nba', name: 'NBA' },
  { id: 'nfl', name: 'NFL' },
];

// Stat filters by sport
const STAT_FILTERS = {
  nhl: [
    { id: 'points', name: 'Points' },
    { id: 'goals', name: 'Goals' },
    { id: 'assists', name: 'Assists' },
    { id: 'shots', name: 'Shots' },
  ],
  nba: [
    { id: 'points', name: 'Points' },
    { id: 'rebounds', name: 'Rebounds' },
    { id: 'assists', name: 'Assists' },
    { id: 'three_point_made', name: '3PT Made' },
    { id: 'double_double', name: 'Double-Double' },
    { id: 'triple_double', name: 'Triple-Double' },
  ],
  nfl: [
    { id: 'passing_yards', name: 'Pass Yds' },
    { id: 'rushing_yards', name: 'Rush Yds' },
    { id: 'receiving_yards', name: 'Rec Yds' },
    { id: 'passing_touchdowns', name: 'Pass TD' },
    { id: 'rushing_touchdowns', name: 'Rush TD' },
    { id: 'receptions', name: 'Receptions' },
  ],
};

// Hit rate filter options
const HIT_RATE_FILTERS = [
  { id: '60', name: '60%+', min: 0.6 },
  { id: '80', name: '80%+', min: 0.8 },
  { id: '100', name: '100%', min: 1.0 },
];

// Games window filter
const GAMES_WINDOW_FILTERS = [
  { id: '3', name: 'Last 3', value: 3 },
  { id: '5', name: 'Last 5', value: 5 },
  { id: '10', name: 'Last 10', value: 10 },
];

// Premium features for gate modal
const PREMIUM_FEATURES = [
  'AI-powered predictions with confidence scores',
  'Player averages vs tonight\'s opponent',
  'Historical matchup analysis',
  'Export data for research',
];

const PAGE_SIZE = 15;

// Dashboard tabs
const DASHBOARD_TABS = [
  { id: 'players', name: 'Player Streaks', icon: 'fire' },
  { id: 'teams', name: 'Team Trends', icon: 'chart' },
];

/**
 * Sortable table header with optional tooltip
 */
function SortHeader({ label, sortKey, currentSort, onSort, align = 'left', tooltip = null }) {
  const isActive = currentSort.key === sortKey;
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState('bottom'); // Default to below since headers are at top
  const tooltipRef = useRef(null);

  // Check viewport position to determine tooltip placement
  const handleMouseEnter = () => {
    if (tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      // If less than 250px from top, show tooltip below; otherwise above
      setTooltipPosition(rect.top < 250 ? 'bottom' : 'top');
    }
    setShowTooltip(true);
  };

  return (
    <th
      className="py-2 px-3 text-[11px] font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary transition-colors select-none"
      onClick={() => onSort(sortKey)}
    >
      <div className={`flex items-center gap-1 ${alignClass}`}>
        <span>{label}</span>
        {tooltip && (
          <div
            ref={tooltipRef}
            className={`relative ${showTooltip ? 'z-50' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <div className="w-3.5 h-3.5 cursor-help" onClick={(e) => e.stopPropagation()}>
              <InformationCircleIcon
                className="w-full h-full text-text-secondary/60 hover:text-accent-blue transition-colors pointer-events-none"
              />
            </div>
            {showTooltip && (
              <div className={`absolute z-[100] left-1/2 -translate-x-1/2 w-64 p-3 bg-bg-primary border border-border rounded-xl shadow-2xl text-left normal-case tracking-normal ${
                tooltipPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
              }`}>
                <div className="text-xs text-text-primary font-medium mb-1">{tooltip.title}</div>
                <div className="text-[11px] text-text-secondary leading-relaxed">{tooltip.description}</div>
                {tooltip.details && (
                  <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                    {tooltip.details.map((detail, i) => (
                      <div key={i} className="flex items-start gap-2 text-[10px]">
                        <span className={detail.color || 'text-text-secondary'}>{detail.icon}</span>
                        <span className="text-text-secondary">{detail.text}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Arrow - points up when below, points down when above */}
                {tooltipPosition === 'top' ? (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-bg-primary border-r border-b border-border"></div>
                ) : (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-bg-primary border-l border-t border-border"></div>
                )}
              </div>
            )}
          </div>
        )}
        {isActive && (
          currentSort.dir === 'asc'
            ? <ArrowUpIcon className="w-3 h-3 text-accent-blue" />
            : <ArrowDownIcon className="w-3 h-3 text-accent-blue" />
        )}
      </div>
    </th>
  );
}

/**
 * Hit rate visual indicator - compact dots in chronological order (oldest left, newest right)
 * Uses trend pattern from streak_details if available, otherwise falls back to counts
 */
function HitDots({ gamesHit, gamesWindow, streakDetails }) {
  // Try to parse streak_details to get the actual pattern
  let pattern = null;
  if (streakDetails) {
    try {
      const details = typeof streakDetails === 'string' ? JSON.parse(streakDetails) : streakDetails;
      pattern = details?.trend?.pattern;
    } catch (e) {
      // Fall back to count-based display
    }
  }

  const dots = [];

  if (pattern) {
    // Pattern is "11101" where leftmost=most recent, so reverse for chronological (oldest left, newest right)
    const reversedPattern = pattern.split('').reverse();
    reversedPattern.forEach((char, i) => {
      const isHit = char === '1';
      dots.push(
        <div
          key={i}
          className={`w-1.5 h-1.5 rounded-full ${isHit ? 'bg-success' : 'bg-danger/40'}`}
          title={isHit ? 'Hit' : 'Miss'}
        />
      );
    });
  } else {
    // Fallback: show hits first, then misses (not ideal but works without pattern data)
    const misses = gamesWindow - gamesHit;
    for (let i = 0; i < gamesHit; i++) {
      dots.push(<div key={`h${i}`} className="w-1.5 h-1.5 rounded-full bg-success" />);
    }
    for (let i = 0; i < misses; i++) {
      dots.push(<div key={`m${i}`} className="w-1.5 h-1.5 rounded-full bg-danger/40" />);
    }
  }

  return <div className="flex gap-0.5">{dots}</div>;
}

/**
 * Calculate edge rating from streak data
 * Returns: 'up' (strong), 'neutral' (fair), 'down' (weak)
 *
 * Edge calculation considers:
 * 1. Base hit rate (60-90%+)
 * 2. Trend direction (hot/improving vs declining/cold)
 * 3. Current consecutive run
 * 4. Streak history risk (100% streak continuation likelihood)
 */
function calculateEdge(streak) {
  let score = 0;

  // Base from hit rate
  if (streak.hit_rate >= 0.9) score += 3;
  else if (streak.hit_rate >= 0.8) score += 2;
  else if (streak.hit_rate >= 0.7) score += 1;
  else if (streak.hit_rate < 0.6) score -= 1;

  // Parse streak_details for trend and streak history
  if (streak.streak_details) {
    try {
      const details = typeof streak.streak_details === 'string'
        ? JSON.parse(streak.streak_details)
        : streak.streak_details;

      // Trend direction
      const direction = details?.trend?.direction;
      if (direction === 'hot') score += 2;
      else if (direction === 'improving') score += 1;
      else if (direction === 'declining') score -= 1;
      else if (direction === 'cold') score -= 2;

      // Current run
      const currentRun = details?.trend?.current_run || 0;
      if (currentRun >= 3) score += 1;
      else if (currentRun <= -3) score -= 1;

      // Streak history risk adjustment (100% streak continuation analysis)
      // This is the key insight: 100% 3-game streaks are common, 5-game are rare
      // If player typically ends streaks around game 4-5, factor that in
      const streakRisk = details?.streak_risk;
      if (streakRisk) {
        const edgeAdjustment = streakRisk.edge_adjustment || 0;
        score += edgeAdjustment;

        // Additional penalty if continuation probability is very low
        if (streakRisk.continuation_probability < 40) {
          score -= 1;
        }
      }
    } catch (e) {
      // If parsing fails, just use hit rate
    }
  }

  // Convert score to rating
  if (score >= 3) return 'up';
  if (score <= 0) return 'down';
  return 'neutral';
}

/**
 * Status badge for streak type
 * Now considers streak risk - a 100% streak at player's ceiling should warn about regression
 */
function StatusBadge({ hitRate, streakDetails }) {
  // Parse streak risk data
  let streakRisk = null;
  let continuationProbability = null;

  if (streakDetails) {
    try {
      const details = typeof streakDetails === 'string' ? JSON.parse(streakDetails) : streakDetails;
      streakRisk = details?.streak_risk;
      continuationProbability = streakRisk?.continuation_probability;
    } catch (e) {
      // Ignore parse errors
    }
  }

  // If 100% hit rate but LOW continuation probability - this is a regression warning!
  if (hitRate >= 1 && continuationProbability !== null && continuationProbability < 45) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-purple-500/20 text-purple-400 border border-purple-500/30" title={`Only ${continuationProbability}% chance to continue`}>
        <ExclamationTriangleIcon className="w-2.5 h-2.5" />
        AT CEILING
      </span>
    );
  }

  // If 100% hit rate with decent continuation probability - truly blazing
  if (hitRate >= 1) {
    // Show continuation % if available (check for both null and undefined)
    const label = continuationProbability != null
      ? `BLAZING ${continuationProbability}%`
      : 'BLAZING';
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white" title={continuationProbability ? `${continuationProbability}% chance to continue` : undefined}>
        <FireIcon className="w-2.5 h-2.5" />
        {label}
      </span>
    );
  }

  if (hitRate >= 0.8) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-success/20 text-success border border-success/30">
        <BoltIcon className="w-2.5 h-2.5" />
        HOT
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-warning/20 text-warning border border-warning/30">
      TREND
    </span>
  );
}

/**
 * Sport badge
 */
function SportBadge({ sport }) {
  const styles = {
    nhl: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    nba: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    nfl: 'bg-green-500/15 text-green-400 border-green-500/30',
  };

  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${styles[sport] || styles.nhl}`}>
      {sport.toUpperCase()}
    </span>
  );
}

/**
 * Multi-select dropdown component
 */
function MultiSelectDropdown({ label, selected, options, onChange, allLabel = 'All' }) {
  const [open, setOpen] = useState(false);

  const displayText = selected.length === 0 || selected.length === options.length
    ? allLabel
    : selected.length === 1
      ? options.find(o => o.id === selected[0])?.name || selected[0]
      : `${selected.length} selected`;

  const toggleOption = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const selectAll = () => {
    onChange([]);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium bg-bg-secondary hover:bg-bg-secondary/80 text-text-primary border border-border transition-all"
      >
        <span className="text-text-secondary">{label}:</span>
        <span className="max-w-[80px] truncate">{displayText}</span>
        <ChevronDownIcon className={`w-3 h-3 text-text-secondary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-20 bg-bg-card border border-border rounded-lg shadow-xl min-w-[140px] py-1">
            <button
              onClick={() => { selectAll(); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                selected.length === 0 ? 'bg-accent-blue/10 text-accent-blue' : 'text-text-primary hover:bg-bg-secondary'
              }`}
            >
              {allLabel}
            </button>
            <div className="border-t border-border my-1" />
            {options.map(option => (
              <button
                key={option.id}
                onClick={() => toggleOption(option.id)}
                className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-2 ${
                  selected.includes(option.id) ? 'bg-accent-blue/10 text-accent-blue' : 'text-text-primary hover:bg-bg-secondary'
                }`}
              >
                <div className={`w-3 h-3 rounded border flex items-center justify-center ${
                  selected.includes(option.id) ? 'bg-accent-blue border-accent-blue' : 'border-border'
                }`}>
                  {selected.includes(option.id) && (
                    <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                {option.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Pagination component
 */
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
      <div className="text-xs text-text-secondary">
        Page {currentPage} of {totalPages}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-1 rounded hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="w-4 h-4 text-text-secondary" />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`w-7 h-7 rounded text-xs font-medium ${
                currentPage === pageNum
                  ? 'bg-accent-blue text-white'
                  : 'hover:bg-bg-secondary text-text-secondary'
              }`}
            >
              {pageNum}
            </button>
          );
        })}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-1 rounded hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon className="w-4 h-4 text-text-secondary" />
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  // Active tab state
  const [activeTab, setActiveTab] = useState('players');

  // Filter state - multi-select (for player streaks)
  const [selectedSports, setSelectedSports] = useState([]);
  const [selectedStats, setSelectedStats] = useState([]);
  const [selectedHitRates, setSelectedHitRates] = useState([]);
  const [selectedGamesWindow, setSelectedGamesWindow] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Sort state
  const [sort, setSort] = useState({ key: 'hit_rate', dir: 'desc' });

  // Data state - Player Streaks
  const [allStreaks, setAllStreaks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data state - Team Streaks (lazy loaded)
  const [teamStreaksData, setTeamStreaksData] = useState(null);
  const [teamStreaksLoading, setTeamStreaksLoading] = useState(false);
  const [teamStreaksError, setTeamStreaksError] = useState(null);
  const [teamStreaksFetched, setTeamStreaksFetched] = useState(false);

  // Active sports (from seasons API)
  const [activeSports, setActiveSports] = useState(DEFAULT_SPORTS);

  // Modal state
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerDetails, setPlayerDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [hasDetailsError, setHasDetailsError] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const isPremium = userProfile?.subscription_tier === 'premium' || userProfile?.subscription_tier === 'pro';

  // Toggle row expansion for edge details
  const toggleRowExpand = (rowKey, e) => {
    e.stopPropagation(); // Don't trigger row click (player modal)
    if (!isPremium) {
      setShowUpgradeModal(true);
      return;
    }
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  // Get available stat filters based on selected sports
  const availableStatFilters = useMemo(() => {
    const sportsToUse = selectedSports.length === 0
      ? activeSports.map(s => s.id)
      : selectedSports;

    const stats = new Map();
    sportsToUse.forEach(sport => {
      (STAT_FILTERS[sport] || []).forEach(stat => {
        stats.set(stat.id, stat);
      });
    });
    return Array.from(stats.values());
  }, [selectedSports, activeSports]);

  // Fetch active sports and all streaks data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // First, fetch active sports from the seasons API
        let sportsToFetch = DEFAULT_SPORTS;
        try {
          const sportsResponse = await api.get('/predictions/active-sports');
          if (sportsResponse.data?.success && sportsResponse.data?.sports?.length > 0) {
            // Map API response to our format
            sportsToFetch = sportsResponse.data.sports.map(s => ({
              id: s.sport,
              name: s.name || s.sport.toUpperCase()
            }));
            setActiveSports(sportsToFetch);
          }
        } catch (sportsErr) {
          console.warn('Could not fetch active sports, using defaults:', sportsErr);
        }

        // Fetch streaks only for active sports
        const streakPromises = sportsToFetch.map(sport =>
          api.get(`/streaks/${sport.id}`, { params: { min_hit_rate: 0.7, limit: 50 } })
            .then(res => ({ sport: sport.id, data: res.data }))
            .catch(() => ({ sport: sport.id, data: { streaks: [] } }))
        );

        const results = await Promise.all(streakPromises);

        // Combine all streaks with sport identifier
        const allStreaksData = results.flatMap(result =>
          (result.data.streaks || []).map(s => ({ ...s, sport: result.sport }))
        );

        setAllStreaks(allStreaksData);

        if (allStreaksData.length === 0 && sportsToFetch.length > 0) {
          // Only set error if we expected data but got none
          const hasAnyData = results.some(r => r.data?.streaks?.length > 0);
          if (!hasAnyData) {
            setError('No streak data available');
          }
        }
      } catch (err) {
        console.error('Error fetching streaks:', err);
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch team streaks data (lazy loaded on tab click)
  const fetchTeamStreaks = async (force = false) => {
    // Don't refetch if already loaded (unless forced)
    if (teamStreaksFetched && !force) return;

    setTeamStreaksLoading(true);
    setTeamStreaksError(null);

    try {
      // Use active sports for team streaks
      const sportsList = activeSports.map(s => s.id).join(',');
      const response = await api.get('/streaks/teams/today', {
        params: { sports: sportsList }
      });
      setTeamStreaksData(response.data);
      setTeamStreaksFetched(true);
    } catch (err) {
      console.error('Error fetching team streaks:', err);
      setTeamStreaksError(err.response?.data?.detail || err.message);
    } finally {
      setTeamStreaksLoading(false);
    }
  };

  // Handle tab change with lazy loading
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    // Lazy load team streaks when first clicking the tab
    if (tabId === 'teams' && !teamStreaksFetched) {
      fetchTeamStreaks();
    }
  };

  // Filter and sort streaks
  const filteredStreaks = useMemo(() => {
    let filtered = [...allStreaks];

    // Sport filter
    if (selectedSports.length > 0) {
      filtered = filtered.filter(s => selectedSports.includes(s.sport));
    }

    // Stat type filter
    if (selectedStats.length > 0) {
      filtered = filtered.filter(s => selectedStats.includes(s.stat_type));
    }

    // Hit rate filter
    if (selectedHitRates.length > 0) {
      const minHitRate = Math.min(...selectedHitRates.map(id => {
        const filter = HIT_RATE_FILTERS.find(f => f.id === id);
        return filter ? filter.min : 0.6;
      }));
      filtered = filtered.filter(s => s.hit_rate >= minHitRate);
    }

    // Games window filter
    if (selectedGamesWindow.length > 0) {
      const windows = selectedGamesWindow.map(id => {
        const filter = GAMES_WINDOW_FILTERS.find(f => f.id === id);
        return filter ? filter.value : null;
      }).filter(Boolean);
      if (windows.length > 0) {
        filtered = filtered.filter(s => windows.includes(s.games_window));
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.player_name?.toLowerCase().includes(query) ||
        s.team_abbreviation?.toLowerCase().includes(query) ||
        s.threshold_description?.toLowerCase().includes(query)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sort.key];
      let bVal = b[sort.key];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (sort.dir === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return filtered;
  }, [allStreaks, selectedSports, selectedStats, selectedHitRates, selectedGamesWindow, searchQuery, sort]);

  // Paginated streaks
  const paginatedStreaks = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredStreaks.slice(start, start + PAGE_SIZE);
  }, [filteredStreaks, currentPage]);

  const totalPages = Math.ceil(filteredStreaks.length / PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSports, selectedStats, selectedHitRates, selectedGamesWindow, searchQuery]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    return {
      total: filteredStreaks.length,
      blazing: filteredStreaks.filter(s => s.hit_rate >= 1).length,
      hot: filteredStreaks.filter(s => s.hit_rate >= 0.8 && s.hit_rate < 1).length,
    };
  }, [filteredStreaks]);

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handlePlayerClick = async (streak) => {
    if (!isPremium) {
      setShowUpgradeModal(true);
      return;
    }

    setSelectedPlayer(streak);
    setPlayerDetails(null);
    setIsLoadingDetails(true);
    setHasDetailsError(false);
    setShowDetailModal(true);

    try {
      const details = await playerDetailsService.getPlayerDetails(
        streak.sport,
        streak.player_id,
        { gamesLimit: 10, includePredictions: false }
      );
      setPlayerDetails(details);
    } catch (error) {
      console.error('Error fetching player details:', error);
      setHasDetailsError(true);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleClosePlayerModal = () => {
    setShowDetailModal(false);
    setSelectedPlayer(null);
    setPlayerDetails(null);
    setHasDetailsError(false);
  };

  const handleGameClick = (e, streak) => {
    e.stopPropagation();
    // Navigate to game analytics page - format: /leagues/{sport}/game/{gameId}/analytics
    if (streak.game_id) {
      navigate(`/leagues/${streak.sport}/game/${streak.game_id}/analytics`);
    }
  };

  const clearFilters = () => {
    setSelectedSports([]);
    setSelectedStats([]);
    setSelectedHitRates([]);
    setSelectedGamesWindow([]);
    setSearchQuery('');
  };

  const hasActiveFilters = selectedSports.length > 0 || selectedStats.length > 0 || selectedHitRates.length > 0 || selectedGamesWindow.length > 0 || searchQuery;

  return (
    <div className="space-y-4">
      {/* Modals */}
      {showDetailModal && (
        <PlayerDetailModal
          isOpen={showDetailModal}
          onClose={handleClosePlayerModal}
          player={selectedPlayer}
          playerDetails={playerDetails}
          isLoading={isLoadingDetails}
          hasError={hasDetailsError}
          sport={selectedPlayer?.sport || 'nhl'}
          streakData={selectedPlayer}
        />
      )}
      {showUpgradeModal && (
        <PremiumGateModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          title="Unlock Player Analytics"
          description="Get deep insights to build smarter parlays with our AI-powered analysis."
          features={PREMIUM_FEATURES}
        />
      )}

      {/* Header with Tabs */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-text-primary">Dashboard</h1>
            <p className="text-sm text-text-secondary">
              {activeTab === 'players' ? (
                <>
                  {loading ? 'Loading...' : `${summaryStats.total} active trends`}
                  {!loading && summaryStats.blazing > 0 && (
                    <span className="ml-2 text-orange-400">{summaryStats.blazing} blazing</span>
                  )}
                  {!loading && summaryStats.hot > 0 && (
                    <span className="ml-2 text-success">{summaryStats.hot} hot</span>
                  )}
                </>
              ) : (
                <>
                  {teamStreaksLoading ? 'Loading...' : `${teamStreaksData?.total_games || 0} games today`}
                </>
              )}
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 p-1 bg-bg-secondary rounded-xl w-fit">
          {DASHBOARD_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-accent-blue text-white shadow-lg shadow-accent-blue/25'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-card'
              }`}
            >
              {tab.icon === 'fire' ? (
                <FireIcon className="w-4 h-4" />
              ) : (
                <ChartBarIcon className="w-4 h-4" />
              )}
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Player Streaks Tab Content */}
      {activeTab === 'players' && (
        <>
          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative w-40">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-secondary" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 bg-bg-secondary border border-border rounded text-xs text-text-primary placeholder-text-secondary focus:outline-none focus:ring-1 focus:ring-accent-blue/50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  <XMarkIcon className="w-3 h-3 text-text-secondary hover:text-text-primary" />
                </button>
              )}
            </div>

            <MultiSelectDropdown
              label="Sport"
              selected={selectedSports}
              options={activeSports}
              onChange={setSelectedSports}
              allLabel="All Sports"
            />

            <MultiSelectDropdown
              label="Stat"
              selected={selectedStats}
              options={availableStatFilters}
              onChange={setSelectedStats}
              allLabel="All Stats"
            />

            <MultiSelectDropdown
              label="Edge"
              selected={selectedHitRates}
              options={HIT_RATE_FILTERS}
              onChange={setSelectedHitRates}
              allLabel="All"
            />

            <MultiSelectDropdown
              label="Games"
              selected={selectedGamesWindow}
              options={GAMES_WINDOW_FILTERS}
              onChange={setSelectedGamesWindow}
              allLabel="L3/L5/L10"
            />

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-2 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                <XMarkIcon className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-text-secondary">
                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading player streaks...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-danger text-sm mb-3">Failed to load data: {error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-1.5 bg-accent-blue text-white rounded text-xs hover:bg-accent-blue/90"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Data Table */}
          {!loading && !error && (
            <div className="rounded-xl border border-border bg-bg-card overflow-hidden shadow-lg">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-bg-secondary border-b border-border">
                    <tr>
                      <SortHeader label="Player" sortKey="player_name" currentSort={sort} onSort={handleSort} />
                      <th className="py-2 px-3 text-[11px] font-semibold text-text-secondary uppercase tracking-wider text-left">Sport</th>
                      <th className="py-2 px-3 text-[11px] font-semibold text-text-secondary uppercase tracking-wider text-left">Game</th>
                      <SortHeader label="Prop" sortKey="threshold_description" currentSort={sort} onSort={handleSort} />
                      <SortHeader label="L3/5/10" sortKey="games_hit" currentSort={sort} onSort={handleSort} align="center" />
                      <SortHeader
                        label="Edge"
                        sortKey="hit_rate"
                        currentSort={sort}
                        onSort={handleSort}
                        align="center"
                        tooltip={{
                          title: "Edge Rating",
                          description: "Our confidence indicator combining hit rate, recent trend, and streak patterns to assess betting value.",
                          details: [
                            { icon: "👍", text: "Strong edge - High hit rate with positive trend", color: "text-success" },
                            { icon: "👎", text: "Weak edge - Declining or at streak ceiling", color: "text-danger" },
                            { icon: "➖", text: "Neutral - Fair value, mixed signals", color: "text-warning" }
                          ]
                        }}
                      />
                      <th className="py-2 px-3 text-[11px] font-semibold text-text-secondary uppercase tracking-wider text-center">Status</th>
                      <th className="py-2 px-3 text-[11px] font-semibold text-text-secondary uppercase tracking-wider text-center w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {paginatedStreaks.map((streak, index) => {
                      const rowKey = `${streak.sport}-${streak.player_id}-${streak.stat_type}-${index}`;
                      const isExpanded = expandedRows.has(rowKey);
                      const edge = calculateEdge(streak);
                      const edgeColor = edge === 'up' ? 'text-success' : edge === 'down' ? 'text-danger' : 'text-warning';

                      // Parse streak risk data for expanded view
                      let streakRisk = null;
                      if (streak.streak_details) {
                        try {
                          const details = typeof streak.streak_details === 'string'
                            ? JSON.parse(streak.streak_details)
                            : streak.streak_details;
                          streakRisk = details?.streak_risk;
                        } catch (e) {
                          // Ignore parse errors
                        }
                      }

                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            onClick={() => handlePlayerClick(streak)}
                            className={`transition-colors cursor-pointer ${
                              isPremium ? 'hover:bg-accent-blue/5' : 'hover:bg-bg-secondary/50'
                            }`}
                          >
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-text-primary text-sm">{streak.player_name}</span>
                                <span className="text-[10px] text-text-secondary">{streak.team_abbreviation}</span>
                              </div>
                            </td>
                            <td className="py-2 px-3">
                              <SportBadge sport={streak.sport} />
                            </td>
                            <td className="py-2 px-3">
                              {streak.opponent ? (
                                <button
                                  onClick={(e) => handleGameClick(e, streak)}
                                  className="text-xs text-accent-blue hover:underline"
                                >
                                  {streak.is_home ? `vs ${streak.opponent}` : `@ ${streak.opponent}`}
                                </button>
                              ) : (
                                <span className="text-xs text-text-secondary">No game</span>
                              )}
                            </td>
                            <td className="py-2 px-3">
                              <span className="text-xs text-accent-blue font-medium">{streak.threshold_description}</span>
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex justify-center">
                                <HitDots gamesHit={streak.games_hit} gamesWindow={streak.games_window} streakDetails={streak.streak_details} />
                              </div>
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex items-center justify-center">
                                {/* Thumb indicator */}
                                {edge === 'up' && (
                                  <svg className="w-4 h-4 text-success" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                                  </svg>
                                )}
                                {edge === 'down' && (
                                  <svg className="w-4 h-4 text-danger" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.106-1.79l-.05-.025A4 4 0 0011.057 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                                  </svg>
                                )}
                                {edge === 'neutral' && (
                                  <svg className="w-4 h-4 text-warning" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex justify-center">
                                <StatusBadge hitRate={streak.hit_rate} streakDetails={streak.streak_details} />
                              </div>
                            </td>
                            <td className="py-2 px-3 text-center">
                              {isPremium ? (
                                <div className="flex items-center justify-center gap-1">
                                  <button className="text-[10px] text-accent-blue hover:underline">
                                    View
                                  </button>
                                  <button
                                    onClick={(e) => toggleRowExpand(rowKey, e)}
                                    className="p-1 rounded hover:bg-bg-secondary transition-colors"
                                  >
                                    <ChevronDownIcon className={`w-4 h-4 text-text-secondary transition-transform ${
                                      isExpanded ? 'rotate-180' : ''
                                    }`} />
                                  </button>
                                </div>
                              ) : (
                                <LockClosedIcon className="w-3 h-3 text-text-tertiary mx-auto" />
                              )}
                            </td>
                          </tr>

                          {/* Expanded Edge Details Row */}
                          {isExpanded && isPremium && (
                            <tr className="bg-bg-secondary/30">
                              <td colSpan={8} className="py-3 px-6">
                                <div className="flex flex-wrap gap-6 text-xs">
                                  {/* Edge Label */}
                                  <div>
                                    <span className="text-text-secondary">Edge: </span>
                                    <span className={`font-bold ${edgeColor}`}>
                                      {edge === 'up' ? 'Strong' : edge === 'down' ? 'Weak' : 'Fair'}
                                    </span>
                                  </div>

                                  {/* ML Prediction */}
                                  {streak.ml_prediction && (
                                    <div>
                                      <span className="text-text-secondary">ML Pred: </span>
                                      <span className="font-bold text-accent-blue">{streak.ml_prediction.toFixed(1)}</span>
                                      {streak.sb_line && (
                                        <span className="text-text-tertiary ml-1">(Line: {streak.sb_line})</span>
                                      )}
                                    </div>
                                  )}

                                  {/* Streak Risk Data */}
                                  {streakRisk && (
                                    <>
                                      <div>
                                        <span className="text-text-secondary">Risk: </span>
                                        <span className={`font-medium ${
                                          streakRisk.risk_level === 'high' ? 'text-danger' :
                                          streakRisk.risk_level === 'elevated' ? 'text-warning' :
                                          'text-success'
                                        }`}>
                                          {streakRisk.risk_level?.toUpperCase()}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-text-secondary">Continuation: </span>
                                        <span className="text-text-primary font-medium">{streakRisk.continuation_probability}%</span>
                                      </div>
                                      {streakRisk.player_avg > 0 && (
                                        <div>
                                          <span className="text-text-secondary">Avg Streak: </span>
                                          <span className="text-text-primary">{streakRisk.player_avg?.toFixed(1)} games</span>
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {/* Risk Factors */}
                                  {streakRisk?.risk_factors && streakRisk.risk_factors.length > 0 && (
                                    <div className="basis-full mt-1">
                                      <span className="text-text-secondary">Factors: </span>
                                      {streakRisk.risk_factors.slice(0, 2).map((factor, i) => (
                                        <span key={i} className="text-warning ml-1">• {factor}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />

              {filteredStreaks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <AdjustmentsHorizontalIcon className="w-10 h-10 text-text-secondary/30 mb-3" />
                  <h3 className="text-base font-medium text-text-primary mb-1">No matching trends</h3>
                  <p className="text-text-secondary text-sm mb-3">Try adjusting your filters</p>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="px-3 py-1.5 bg-accent-blue text-white rounded text-xs hover:bg-accent-blue/90"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Team Trends Tab Content */}
      {activeTab === 'teams' && (
        <TeamStreaksTable
          teamsData={teamStreaksData}
          loading={teamStreaksLoading}
          error={teamStreaksError}
          onRefresh={() => fetchTeamStreaks(true)}
        />
      )}
    </div>
  );
}
